import { RequestAddOnDto, SubscriptionAddOnPriceOverviewDto } from "../dto/createSubscriptionAddOn.dto";
import { Product } from "./models";
import { BillingIntervalInMonths } from "./subscription.model";

export const productStepUnit = 100_000;
export type ProductPricePerUnit = Record<Partial<Product>, number>;
export const productPricePerUnit: ProductPricePerUnit = {
  [Product.default]: 40,
  [Product.orderbook]: 20,
  [Product.swap]: 20,
  [Product.balance]: 40,
  [Product.price]: 20,
  [Product.token]: 20,
  [Product.txGateway]: 20,
  [Product.portfolio]: 40,
  [Product.gasPrice]: 20,
  [Product.nft]: 20,
  [Product.traces]: 25,
  [Product.fusion]: 20,
  [Product.fusionPlus]: 20,
  [Product.history]: 25,
  [Product.web3]: 15,
  [Product.domain]: 15,
  [Product.tokenDetails]: 40,
  [Product.charts]: 25
};

export type FeesModel = {
  normalPairFee: number;
  stablePairFee: number;
};

export const getBpsByVol = (volume: number): FeesModel => {
  if (volume < 1_000_000) {
    return { normalPairFee: 0.3, stablePairFee: 0.1 };
  } else if (volume < 3_000_000) {
    return { normalPairFee: 0.2, stablePairFee: 0.07 };
  } else if (volume < 7_000_000) {
    return { normalPairFee: 0.1, stablePairFee: 0.04 };
  } else {
    return { normalPairFee: 0.05, stablePairFee: 0.02 };
  }
};

export const getDiscountByVol = (volume: number): number => {
  if (volume < 1000000) {
    return 0;
  } else if (volume < 3000000) {
    return 0.3;
  } else if (volume <= 7000000) {
    return 0.4;
  } else if (volume > 7000000) {
    return 0.5;
  }
  return 0;
};

export const rpsMin = 1;
export const rpsMax = 100;
export const rpsMultiplier = 5;
export function validateRps(rps: number): boolean {
  if (rps < rpsMin || rps > rpsMax) {
    throw new Error(`RPS must be between ${rpsMin} and ${rpsMax}`);
  }
  if (rps === 1) {
    return true;
  }
  if (rps % rpsMultiplier !== 0) {
    throw new Error(`RPS must be a multiple of ${rpsMultiplier}`);
  }
  return true;
}
export const pricePerIncrement = 10;
export const rpsIncrement = 5;
export const priceAlgorithm = (
  product: Product,
  quantity: number,
  rps: number,
  interval: BillingIntervalInMonths
): {
  totalPrice: number;
  monthlyPrice: number;
  discountedPrice: number;
} => {
  const pricePerUnit = productPricePerUnit[product];

  let rpsExtraCost = 0;
  if (!pricePerUnit) {
    throw new Error(`Product ${product} not found`);
  }
  if (rps >= 5) {
    rpsExtraCost = Math.floor(rps / rpsIncrement) * pricePerIncrement;
  }
  const totalPrice = ((pricePerUnit * quantity) / 100000 + rpsExtraCost) * interval;
  const monthlyPrice = (pricePerUnit * quantity) / 100000 + rpsExtraCost;
  const totalDiscountedPrice = totalPrice * (1 - discountByBillingInterval[interval]);

  return {
    totalPrice: totalPrice,
    monthlyPrice: monthlyPrice,
    discountedPrice: totalDiscountedPrice
  };
};

export const addOnPricePerUnit = 1;
export const addOnPrice = (
  addONConfig: RequestAddOnDto,
  billingInterval: BillingIntervalInMonths
): SubscriptionAddOnPriceOverviewDto => {
  const apiKeyCount = Number(addONConfig.apiKeyCount) || 0;
  const applicationCount = Number(addONConfig.applicationCount) || 0;
  const userCount = Number(addONConfig.userCount) || 0;

  const getPrice = (itemCo: number) => {
    return itemCo > 1 ? itemCo - 1 * addOnPricePerUnit : 0;
  };

  const apiKeyPrice = getPrice(apiKeyCount);
  const applicationPrice = getPrice(applicationCount);
  const userPrice = getPrice(userCount);
  const addOnConfig = {
    apiKey: {
      count: apiKeyCount,
      totalPrice: apiKeyPrice * billingInterval,
      discountedPrice: apiKeyPrice * (1 - discountByBillingInterval[billingInterval]) * billingInterval,
      monthlyPrice: apiKeyPrice
    },
    application: {
      count: applicationCount,
      totalPrice: applicationPrice * billingInterval,
      discountedPrice: applicationPrice * (1 - discountByBillingInterval[billingInterval]) * billingInterval,
      monthlyPrice: applicationPrice
    },
    user: {
      count: userCount,
      totalPrice: userPrice * billingInterval,
      discountedPrice: userPrice * (1 - discountByBillingInterval[billingInterval]) * billingInterval,
      monthlyPrice: userPrice
    }
  };
  const totalPrice = addOnConfig.apiKey.totalPrice + addOnConfig.application.totalPrice + addOnConfig.user.totalPrice;
  const discountedPrice =
    addOnConfig.apiKey.discountedPrice + addOnConfig.application.discountedPrice + addOnConfig.user.discountedPrice;
  return {
    totalPrice,
    discountedPrice,
    monthlyPrice:
      addOnConfig.apiKey.monthlyPrice + addOnConfig.application.monthlyPrice + addOnConfig.user.monthlyPrice,
    addOnConfig,
    billingIntervalDiscount: totalPrice - discountedPrice
  };
};

export const discountByBillingInterval: Record<BillingIntervalInMonths, number> = {
  [BillingIntervalInMonths.One]: 0,
  [BillingIntervalInMonths.Three]: 0.05,
  [BillingIntervalInMonths.Six]: 0.075,
  [BillingIntervalInMonths.Twelve]: 0.1
};
